import React, { ReactText } from 'react';
import DeckGL from '@deck.gl/react';
import ReactMapGL, { ContextViewStateChangeInfo, ViewportProps } from 'react-map-gl';
import Hotkeys from 'react-hot-keys';

import {
  mapBoxToken,
  deselectDuration,
  selectDelayDuration,
  resizeDebounceTime,
} from './constants';
import * as documentedAirports from './data/documentedAirports';
import { getViewportDimension } from './utils';
import { FlightData, getFlightData } from './utils/getFlightData';
import { flightLayer } from './layers/flightLayer';
import { Widget } from './components/Widget';
import { MakersDenLogo } from './components/MakersDenLogo';

import { AirportKey } from './data/airports';
import { airportLayer } from './layers/airportLayer';
import { DeckState } from './typings';
import { throws } from 'assert';

interface AppState {
  flightData: null | FlightData;
  airportKey: AirportKey;
  viewState: Partial<ViewportProps> & {
    onTransitionEnd?: () => void;
  };
  deckState: DeckState;
  showFlightLayer: boolean;
  showWidget: boolean;
}

interface AppProps {}

const shortcuts = {
  TOGGLE_FLIGHT_LAYER: "alt+1",
  TOGGLE_WIDGET: "alt+2"
}

const shortcutValues = Object.values(shortcuts).join(",")

export class App extends React.Component<AppProps, AppState> {
  /**
   * resize update timeout
   */
  resizeTimeout: number = 0;

  // airports in use
  documentedAirports = [
    documentedAirports.europe,
    documentedAirports.berlinTop,
    documentedAirports.tempelhof,
    documentedAirports.tegel,
    documentedAirports.schoenefeld,
    documentedAirports.berlinBradenburg,
  ];

  /**
   * only physical airports
   */
  documentedPhysicalAirports = this.documentedAirports.filter(
    current => current.location.isPhysicalAirport,
  );

  constructor(props: AppProps) {
    super(props);

    this.state = {
      deckState: 'selected',
      viewState: {
        ...this.documentedAirports[0].location,
        ...getViewportDimension(),
        transitionDuration: 200,
      },
      flightData: null,
      airportKey: this.documentedAirports[0].location.key,
      showFlightLayer: true,
      showWidget: true
    };

    this.onViewStateChange = this.onViewStateChange.bind(this);
    this.onWebglInitialized = this.onWebglInitialized.bind(this);
    this.onAirportChange = this.onAirportChange.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
    this.toggleFlightLayer = this.toggleFlightLayer.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onToggleWidget = this.onToggleWidget.bind(this);
  }

  onViewStateChange(props: ContextViewStateChangeInfo) {
    const { viewState } = props;
    this.setState(prev => {
      return {
        ...prev,
        viewState: {
          ...prev.viewState,
          latitude: viewState.latitude,
          longitude: viewState.longitude,
          zoom: viewState.zoom,
          pitch: viewState.pitch,
          altitude: viewState.altitude,
        },
      };
    });
  }

  /**
   * handles web gl initialization
   */
  onWebglInitialized() {
    Promise.all([getFlightData()]).then(result => {
      this.setState(prev => {
        return {
          ...prev,
          flightData: result[0],
        };
      });
    });
  }

  /**
   * deselects the airport
   */
  deselectAirport() {
    return new Promise(resolve => {
      this.setState(
        prev => {
          return {
            ...prev,
            deckState: 'deselecting',
          };
        },
        () => {
          setTimeout(resolve, deselectDuration);
        },
      );
    });
  }

  /**
   * selects an airport
   * @param newAirport
   */
  selectAirport(airport: documentedAirports.DocumentedAirportProps) {
    return new Promise(resolve => {
      this.setState(prev => {
        return {
          ...prev,
          deckState: 'selecting',
          viewState: {
            ...prev.viewState,
            ...airport.location,
            transitionDuration: selectDelayDuration,
            onTransitionEnd: resolve,
          },
        };
      });
    }).then(
      () =>
        new Promise(resolve => {
          this.setState(prev => {
            return {
              ...prev,
              deckState: 'selected',
              airportKey: airport.location.key,
              viewState: {
                ...prev.viewState,
                transitionDuration: 200,
                onTransitionEnd: () => {},
              },
            };
          }, resolve);
        }),
    );
  }

  /**
   * set location key
   */
  onAirportChange(newAirport: documentedAirports.DocumentedAirportProps) {
    this.deselectAirport()
      .then(() => {
        console.log('after deselecting', {
          ...this.state,
        });
        return this.selectAirport(newAirport);
      })
      .then(() => {
        console.log('after selecting', {
          ...this.state,
        });
      });
  }

  updateViewportDimension() {
    this.setState(prev => {
      return {
        ...prev,
        viewState: {
          ...prev.viewState,
          ...getViewportDimension(),
        },
      };
    });
  }

  /**
   * react to resize event
   */
  onViewportResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      this.setState(prev => {
        return {
          ...prev,
          viewState: {
            ...prev.viewState,
            ...getViewportDimension(),
          },
        };
      });
    }, resizeDebounceTime);
  }

  /**
   * setup on resize handler
   */
  componentDidMount() {
    window.addEventListener('resize', this.onViewportResize, false);
    this.updateViewportDimension();
  }

  /**
   * remove resize handler
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.onViewportResize, false);
  }

  toggleFlightLayer() {

  }

  onToggleWidget() {
    this.setState(({showWidget}) => ({
      showWidget: !showWidget
    }));
  }

  onKeyUp(keyName: string) {
    if (keyName === shortcuts.TOGGLE_FLIGHT_LAYER) {
      this.setState(({ showFlightLayer }) => ({
        showFlightLayer: !showFlightLayer
      }));
      console.log("togggle");
    }

    if (keyName === shortcuts.TOGGLE_WIDGET) {
      this.onToggleWidget();
    }
  }

  render() {
    const { viewState, flightData, airportKey, deckState, showWidget, showFlightLayer } = this.state;
    const layers = flightData && showFlightLayer
      ? [
          flightLayer(flightData as any),
          airportLayer(this.documentedPhysicalAirports, airportKey, deckState),
        ]
      : [];
    return (
      <React.Fragment>
        <DeckGL
          viewState={viewState}
          controller={true}
          width={viewState.width as ReactText}
          height={viewState.height as ReactText}
          layers={layers}
          onViewStateChange={this.onViewStateChange}
          onWebGLInitialized={this.onWebglInitialized}
        >
          <ReactMapGL
            mapboxApiAccessToken={mapBoxToken}
            mapStyle='mapbox://styles/kallebertell/cjbhj191scvc02rmxgjbgtzju'
            dragRotate={true}
            dragPan={true}
            width={viewState.width as ReactText}
            height={viewState.height as ReactText}
          />
        </DeckGL>
        <Widget
          onToggleWidget={this.onToggleWidget}
          visible={showWidget}
          airports={this.documentedAirports}
          currentAirportKey={airportKey}
          deckState={deckState}
          onAirportChange={this.onAirportChange}
        />
        <MakersDenLogo />
        <Hotkeys
          keyName={shortcutValues}
          onKeyUp={this.onKeyUp}
        />
      </React.Fragment>
    );
  }
}
